import ChatsList from 'components/chats/ChatsList';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Stepper } from 'components/ui/stepper';
import { useReferentCaregiver } from 'hooks/useCaregivers';
import { useConversationsByType } from 'hooks/useConversations';
import usePreferences from 'hooks/usePreferences';
import useStartConversation from 'hooks/useStartConversation';
import { cn } from 'lib/utils';
import { ClockIcon, LockIcon, MessageCircleIcon, Repeat2Icon } from 'lucide-react';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import GoodToKnow from './GoodToKnow';
import MessagingStories from './MessagingStories';
import PrivacyAlert from './PrivacyAlert';
import ReferentHero from './ReferentHero';
import TeamSection from './TeamSection';

/** `?stories=1` opens the stories over the messaging page (the homepage card links there the first time). */
export const STORIES_SEARCH_PARAM = 'stories';

/**
 * The patient's messaging page in the redesign (Figma 3a-Messaging and
 * 5-MessagingMultiple): her conversations, or her referring caregiver and how
 * to start, then privacy, how it works, the team and what is good to know.
 */
export default function MessagingHomeV3({
  scrollableElement,
}: {
  scrollableElement: React.RefObject<HTMLDivElement | null>;
}) {
  const { t } = useTranslation();
  const conversations = useConversationsByType('assigned');
  const { referent } = useReferentCaregiver();
  const { startConversation, paywall } = useStartConversation('messaging_page');
  const [searchParams, setSearchParams] = useSearchParams();
  const { set: setStoriesSeen } = usePreferences(Keys.MESSAGING_STORIES_SEEN);
  const hasConversations = conversations.length > 0;

  const storiesOpen = searchParams.get(STORIES_SEARCH_PARAM) === '1';
  const openStories = () => {
    posthog.capture('messaging.stories_opened', { source: 'more_details' });
    setSearchParams({ [STORIES_SEARCH_PARAM]: '1' });
  };
  const closeStories = () => {
    setStoriesSeen('true');
    setSearchParams({}, { replace: true });
  };

  // A fade at the bottom tells there is more below, until the end of the page
  const [hasMoreBelow, setHasMoreBelow] = useState(false);
  useEffect(() => {
    const el = scrollableElement.current;
    if (!el) return;
    // Gone once only the page's bottom padding (pb-28) is left below, so it never covers the last lines
    const update = () => setHasMoreBelow(el.scrollTop + el.clientHeight < el.scrollHeight - 112);
    update();
    el.addEventListener('scroll', update, { passive: true });
    // The page grows as its sections load
    const observer = new ResizeObserver(update);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [scrollableElement]);

  // The call to action follows the patient down the page once its place in the hero is out of view
  const ctaRef = useRef<HTMLButtonElement>(null);
  const [showFloatingCta, setShowFloatingCta] = useState(false);
  useEffect(() => {
    const el = ctaRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => setShowFloatingCta(!entry.isIntersecting), {
      root: scrollableElement.current,
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasConversations, scrollableElement]);

  return (
    // The bottom padding keeps the last lines clear of the floating call to action (and of the fade, below)
    <div className="flex w-full flex-col pb-28" data-testid="messaging-home-v3">
      {hasConversations ? (
        <div className="bg-card pt-2 shadow-xs">
          <ChatsList
            conversations={conversations}
            title={null}
            readOnlyConvs={false}
            referent={referent}
            unassignedRecipient={referent}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-10 rounded-b-[2rem] bg-card px-6 py-8 shadow-sm">
          <ReferentHero referent={referent} />
          <div className="flex flex-col gap-5">
            <Button ref={ctaRef} size="xl" className="w-full" onClick={startConversation}>
              {t('messagingV3.startConversation')}
            </Button>
            {/* A few reassuring words, explained further down the page */}
            <ul className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground [&_svg]:size-3.5">
              <li className="flex items-center gap-1.5">
                <LockIcon aria-hidden />
                {t('messagingV3.hero.confidential')}
              </li>
              <li className="flex items-center gap-1.5">
                <MessageCircleIcon aria-hidden />
                {t('messagingV3.hero.noChatbot')}
              </li>
              <li className="flex items-center gap-1.5">
                <ClockIcon aria-hidden />
                {t('messagingV3.hero.within24h')}
              </li>
            </ul>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-14 px-4 pt-12">
        <PrivacyAlert />

        <section className="flex flex-col gap-6">
          <h4 className="px-2">{t('messagingV3.howItWorks.title')}</h4>
          <div className="px-6">
            <Stepper
              steps={[
                { label: t('messagingV3.howItWorks.write') },
                { label: t('messagingV3.howItWorks.answer', { firstName: referent.firstName }) },
                { label: t('messagingV3.howItWorks.orient') },
              ]}
            />
          </div>
          {hasConversations && (
            <Alert variant="outline" role="note">
              <Repeat2Icon aria-hidden />
              <AlertDescription className="text-foreground">
                {t('messagingV3.howItWorks.otherQuestions')}
              </AlertDescription>
            </Alert>
          )}
          <Button variant="outline" className="self-center" onClick={openStories}>
            {t('messagingV3.howItWorks.moreDetails')}
          </Button>
        </section>

        <TeamSection />

        <GoodToKnow />
      </div>

      {scrollableElement.current?.parentElement &&
        createPortal(
          // Portalled into the page (position: relative), over the scroll content's bottom edge, under the call to action
          <div
            aria-hidden
            data-testid="messaging-home-more-below"
            data-visible={hasMoreBelow}
            className={cn(
              'pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-linear-to-t from-background to-background/0 transition-opacity duration-200',
              hasMoreBelow ? 'opacity-100' : 'opacity-0',
            )}
          />,
          scrollableElement.current.parentElement,
        )}

      {showFloatingCta &&
        !hasConversations &&
        scrollableElement.current?.parentElement &&
        createPortal(
          // Portalled into the page (position: relative) so it sits above the scroll content but below the nav bar
          <div className="absolute right-4 bottom-4 left-4 z-10 flex flex-col items-center gap-2 drop-shadow-md animate-in fade-in slide-in-from-bottom-2 duration-200">
            <Button size="xl" className="w-full" onClick={startConversation}>
              {t('messagingV3.startConversation')}
            </Button>
          </div>,
          scrollableElement.current.parentElement,
        )}

      <MessagingStories
        open={storiesOpen}
        referent={referent}
        onClose={closeStories}
        onStart={() => {
          closeStories();
          startConversation();
        }}
      />
      {paywall}
    </div>
  );
}
